import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { BackSide, Color } from 'three';
import { hourOfDay, sunStateForHour } from './dayNightCycle';

const VERT = /* glsl */ `
  varying vec3 vWorldPos;
  void main() {
    vec4 world = modelMatrix * vec4(position, 1.0);
    vWorldPos = world.xyz;
    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;

const FRAG = /* glsl */ `
  uniform vec3 uZenith;
  uniform vec3 uHorizon;
  varying vec3 vWorldPos;
  void main() {
    float h = normalize(vWorldPos).y;
    float t = clamp(h * 0.5 + 0.5, 0.0, 1.0);
    t = pow(t, 0.85);
    vec3 col = mix(uHorizon, uZenith, t);
    gl_FragColor = vec4(col, 1.0);
  }
`;

/**
 * Large gradient sky dome. Colors ease toward day/night targets each frame.
 * @param {number} tick
 */
export function SkyDome({ tick = 0 }) {
  const matRef = useRef();
  const initial = useMemo(() => sunStateForHour(hourOfDay(tick)), []); // eslint-disable-line react-hooks/exhaustive-deps
  const targetZenith = useMemo(() => new Color(), []);
  const targetHorizon = useMemo(() => new Color(), []);

  // Memoize uniforms so React doesn't recreate them each render (which resets lerp).
  const uniforms = useMemo(() => ({
    uZenith: { value: new Color(initial.skyColor) },
    uHorizon: { value: new Color(initial.horizonColor) },
  }), [initial.skyColor, initial.horizonColor]);

  useFrame((_, delta) => {
    const mat = matRef.current;
    if (!mat?.uniforms) return;
    const target = sunStateForHour(hourOfDay(tick));
    const k = 1 - Math.exp(-2.5 * delta);
    targetZenith.set(target.skyColor);
    targetHorizon.set(target.horizonColor);
    mat.uniforms.uZenith.value.lerp(targetZenith, k);
    mat.uniforms.uHorizon.value.lerp(targetHorizon, k);
  });

  return (
    <mesh frustumCulled={false}>
      <sphereGeometry args={[120, 32, 16]} />
      <shaderMaterial
        ref={matRef}
        vertexShader={VERT}
        fragmentShader={FRAG}
        side={BackSide}
        depthWrite={false}
        uniforms={uniforms}
      />
    </mesh>
  );
}
